// Points d'intérêt pour la carte ferroviaire
import maplibregl, { type Map } from 'maplibre-gl';
import { pkLoader, type PKPoint } from '../data/pk-loader';

export interface PoiData {
  id: string;
  type: 'station' | 'pk' | 'signal' | 'junction' | 'depot';
  name: string;
  coordinates: [number, number]; // [lon, lat]
  properties?: Record<string, any>;
}

// Interface PKData maintenant définie dans pk-loader.ts
// interface PKData { ... }

// Ancienne fonction parseCSV - maintenant remplacée par le système régional
// function parseCSV(csvText: string): PKData[] { ... }

// Chargement des gares uniquement (une seule fois)
async function loadStationsData(): Promise<PoiData[]> {
  try {
    console.log('📡 Début chargement des gares...');
    
    const localitesResponse = await fetch('/localites.geojson');
    if (!localitesResponse.ok) {
      throw new Error(`HTTP ${localitesResponse.status}: ${localitesResponse.statusText}`);
    }
    
    console.log('📡 Parsing JSON gares...');
    const localitesData = await localitesResponse.json();
    
    console.log(`📍 ${localitesData.features?.length || 0} gares chargées depuis le serveur`);
    
    const stations: PoiData[] = localitesData.features?.map((feature: any, index: number) => {
      if (index < 3) {
        console.log(`🔍 Exemple gare ${index}: ${feature.properties?.libelle} à [${feature.geometry.coordinates}]`);
      }
      return {
        id: feature.properties.code_uic || feature.properties.code || `station-${Math.random()}`,
        type: 'station' as const,
        name: feature.properties.libelle || feature.properties.nom || 'Gare inconnue',
        coordinates: feature.geometry.coordinates as [number, number],
        properties: {
          uic: feature.properties.code_uic,
          code: feature.properties.code,
          ligne: feature.properties.ligne,
          pk: feature.properties.pk,
          departement: feature.properties.departement
        }
      };
    }) || [];
    
    console.log(`✅ ${stations.length} gares traitées pour MapLibre`);
    return stations;
    
  } catch (error) {
    console.error('❌ Erreur chargement gares:', error);
    return [
      { id: 'paris-est', type: 'station', name: 'Paris-Est', coordinates: [2.3598, 48.8768] },
      { id: 'metz-ville', type: 'station', name: 'Metz-Ville', coordinates: [6.1667, 49.1167] },
      { id: 'strasbourg', type: 'station', name: 'Strasbourg', coordinates: [7.7336, 48.5847] },
      { id: 'nancy', type: 'station', name: 'Nancy-Ville', coordinates: [6.1745, 48.6903] },
      { id: 'forbach', type: 'station', name: 'Forbach', coordinates: [6.9008, 49.1900] }
    ];
  }
}

// Convertir PKPoint en PoiData
function convertPKsToPoiData(pkPoints: PKPoint[]): PoiData[] {
  return pkPoints.map((pk) => ({
    id: pk.id,
    type: 'pk' as const,
    name: `PK${pk.pk}`, // Format simplifié : PK19 au lieu de PK 19 (110000)
    coordinates: [pk.lon, pk.lat] as [number, number],
    properties: {
      code_ligne: pk.code_ligne,
      pk: pk.pk,
      vitesse: pk.vitesse,
      altitude: pk.altitude
    }
  }));
}

export class PoiLayer {
  private map: Map | null = null;
  private visible: boolean = false;
  private layerId = 'railway-pois';
  private sourceId = 'railway-pois-source';
  private pkSourceId = 'railway-pks-source';
  private lastUpdateBounds: maplibregl.LngLatBounds | null = null;
  private lastUpdateZoom: number = 0;
  private updateTimeout: number | null = null;
  private reloadInterval: number | null = null;
  private reloadFrequencyMultiplier: number = 1;
  private currentBearing: number = 0;

  setMap(map: Map) {
    this.map = map;
    this.initializeLayer();
    this.setupDynamicLoading();
  }

  private async initializeLayer() {
    if (!this.map) return;
    
    // Vérifier que le style est chargé
    if (!this.map.isStyleLoaded()) {
      console.log('Style pas encore chargé, POI layer reporté');
      return;
    }

    // Vérifier si déjà initialisé (toutes les couches existent)
    if (this.map.getSource(this.sourceId) && 
        this.map.getLayer(this.layerId) && 
        this.map.getLayer(this.layerId + '-labels') && 
        this.map.getLayer(this.layerId + '-pk-labels')) {
      console.log('POI layer déjà complètement initialisé');
      return;
    }
    
    try {
      // Charger uniquement les gares (une seule fois)
      const stationsData = await loadStationsData();

      // Source pour les gares (statique) - seulement si elle n'existe pas
      if (!this.map.getSource(this.sourceId)) {
        this.map.addSource(this.sourceId, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: stationsData.map(poi => ({
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: poi.coordinates
              },
              properties: {
                id: poi.id,
                type: poi.type,
                name: poi.name,
                ...poi.properties
              }
            }))
          }
        });
        console.log('✅ Source gares créée');
      }

      // Source pour les PK (dynamique) - seulement si elle n'existe pas
      if (!this.map.getSource(this.pkSourceId)) {
        this.map.addSource(this.pkSourceId, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [] // Vide au départ, chargé dynamiquement
          }
        });
        console.log('✅ Source PK créée');
      }

    // Ajouter la couche pour les gares avec l'icône bâtiment ■
    if (!this.map.getLayer(this.layerId)) {
      this.map.addLayer({
        id: this.layerId,
        type: 'symbol',
        source: this.sourceId,
        filter: ['==', ['get', 'type'], 'station'],
        layout: {
          'visibility': 'none', // Masqué par défaut
          'text-field': '■',
          'text-size': 64, // Quadruplé pour être bien visible
          'text-anchor': 'center',
          'text-offset': [0, 0.3] // Abaissé un peu
        },
        paint: {
          'text-color': '#8B4513' // Couleur marron pour le bâtiment
        }
      });
      console.log('✅ Couche gares créée avec icône bâtiment');
    }
    
    // Pas de pastilles pour les PK, uniquement les labels texte

    // Ajouter les labels pour les gares - seulement si elle n'existe pas
    if (!this.map.getLayer(this.layerId + '-labels')) {
      this.map.addLayer({
        id: this.layerId + '-labels',
        type: 'symbol',
        source: this.sourceId,
        filter: ['==', ['get', 'type'], 'station'],
        layout: {
          'visibility': 'none',
          'text-field': ['get', 'name'],
          'text-font': ['Open Sans Bold'],
          'text-size': 20, // Taille correcte et lisible
          'text-offset': [1.0, 0.2], // Plus près de la position GPS
          'text-anchor': 'left'
        },
        paint: {
          'text-color': '#4C1D95', // Violet sombre
          'text-halo-color': '#FFFFFF', // Contour blanc
          'text-halo-width': 2
        }
      });
      console.log('✅ Couche labels gares créée en violet sombre');
    }

    // Ajouter les labels pour les PK - UNIQUEMENT DU TEXTE, pas de pastilles
    if (!this.map.getLayer(this.layerId + '-pk-labels')) {
      this.map.addLayer({
        id: this.layerId + '-pk-labels',
        type: 'symbol',
        source: this.pkSourceId,
        layout: {
          'visibility': 'none',
          'text-field': ['get', 'name'],
          'text-font': ['Open Sans Bold'], // Bold pour plus de lisibilité
          'text-size': 16, // Taille encore plus grande pour être bien visible
          'text-anchor': 'center' // Centré directement sur la position
        },
        paint: {
          'text-color': '#1E40AF', // Bleu plus foncé pour meilleure lisibilité
          'text-halo-color': '#FFFFFF',
          'text-halo-width': 3 // Halo encore plus épais pour contraste maximum
        }
      });
      console.log('✅ Couche labels PK créée');
    }

    // Gestionnaire de clic commun pour les deux couches
    const handleClick = (e: any) => {
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        const props = feature.properties;
        
        console.log(`📍 POI cliqué: ${props?.name} (${props?.type})`);
        
        let popupContent = '';
        if (props?.type === 'station') {
          const uic = props?.uic ? `<p style="margin: 0; font-size: 10px; color: #888;">UIC: ${props.uic}</p>` : '';
          const code = props?.code ? `<p style="margin: 0; font-size: 10px; color: #888;">Code: ${props.code}</p>` : '';
          const dept = props?.departement ? `<p style="margin: 0; font-size: 10px; color: #888;">Dép: ${props.departement}</p>` : '';
          
          popupContent = `
            <div style="padding: 8px; max-width: 200px;">
              <h3 style="margin: 0 0 4px 0; color: #333; font-size: 14px;">${props.name}</h3>
              <p style="margin: 0 0 4px 0; font-size: 11px; color: #666;">🚉 Station ferroviaire</p>
              ${uic}${code}${dept}
            </div>
          `;
        } else if (props?.type === 'pk') {
          const vitesse = props?.vitesse ? `<p style="margin: 0; font-size: 10px; color: #888;">Vitesse: ${props.vitesse} km/h</p>` : '';
          const altitude = props?.altitude ? `<p style="margin: 0; font-size: 10px; color: #888;">Alt: ${props.altitude}m</p>` : '';
          
          popupContent = `
            <div style="padding: 6px; max-width: 180px;">
              <h3 style="margin: 0 0 4px 0; color: #333; font-size: 13px;">${props.name}</h3>
              <p style="margin: 0 0 4px 0; font-size: 10px; color: #666;">🛤️ Point kilométrique</p>
              ${vitesse}${altitude}
            </div>
          `;
        }
        
        new maplibregl.Popup({ offset: 25 })
          .setLngLat(e.lngLat)
          .setHTML(popupContent)
          .addTo(this.map!);
      }
    };

    // Ajouter les événements de clic pour les gares et les labels PK
    this.map.on('click', this.layerId, handleClick);
    this.map.on('click', this.layerId + '-pk-labels', handleClick); // Clic sur les labels PK

    // Changer le curseur au survol pour les gares et labels PK
    this.map.on('mouseenter', this.layerId, () => {
      if (this.map) this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseenter', this.layerId + '-pk-labels', () => {
      if (this.map) this.map.getCanvas().style.cursor = 'pointer';
    });

    this.map.on('mouseleave', this.layerId, () => {
      if (this.map) this.map.getCanvas().style.cursor = '';
    });
    this.map.on('mouseleave', this.layerId + '-pk-labels', () => {
      if (this.map) this.map.getCanvas().style.cursor = '';
    });
    
    } catch (error) {
      console.error('Erreur lors de l\'initialisation de la couche POI:', error);
    }
  }

  show() {
    if (!this.map || this.visible) return;
    
    console.log('🔍 Activation POI - début');
    
    try {
      // Vérifier que les sources existent
      const stationSource = this.map.getSource(this.sourceId);
      const pkSource = this.map.getSource(this.pkSourceId);
      
      console.log('🔍 Sources disponibles:', {
        stations: !!stationSource,
        pks: !!pkSource
      });
      
      // Si les sources n'existent pas, forcer la réinitialisation
      if (!stationSource || !pkSource) {
        console.log('⚠️ Sources manquantes, réinitialisation forcée...');
        this.initializeLayer();
        // Attendre un peu puis réessayer
        setTimeout(() => {
          if (this.map) {
            this.show();
          }
        }, 1000);
        return;
      }
      
      if (stationSource) {
        // Vérifier que les couches gares existent et les rendre visibles
        if (this.map.getLayer(this.layerId)) {
          this.map.setLayoutProperty(this.layerId, 'visibility', 'visible');
          console.log('🏚️ Icônes gares rendues visibles');
        }
        if (this.map.getLayer(this.layerId + '-labels')) {
          this.map.setLayoutProperty(this.layerId + '-labels', 'visibility', 'visible');
          console.log('🏷️ Labels gares rendus visibles');
        }
        console.log('📍 Gares complètement visibles (icônes + labels)');
      }
      
      if (pkSource) {
        // Vérifier que la couche PK labels existe avant de l'afficher
        if (this.map.getLayer(this.layerId + '-pk-labels')) {
          this.map.setLayoutProperty(this.layerId + '-pk-labels', 'visibility', 'visible');
          console.log('🛤️ Labels PK rendus visibles (pas de pastilles)');
        } else {
          console.warn('⚠️ Couche PK labels non trouvée lors de l\'activation');
        }
        
        // Toujours déclencher le chargement initial des PK si la source existe
        const bounds = this.map.getBounds();
        const zoom = this.map.getZoom();
        console.log('🔍 Déclenchement chargement PK initial:', { zoom });
        // Forcer la mise à jour même si les bounds sont identiques
        this.lastUpdateBounds = null;
        this.lastUpdateZoom = -1;
        this.updatePKData(bounds, zoom);
      }
      
      this.visible = true;
      this.startPeriodicReload();
      console.log('✅ Points d\'intérêt affichés (gares + PK) + rechargement automatique démarré');
      
    } catch (error) {
      console.error('❌ Erreur activation POI:', error);
    }
  }

  hide() {
    if (!this.map || !this.visible) return;
    
    try {
      if (this.map.getLayer(this.layerId)) {
        this.map.setLayoutProperty(this.layerId, 'visibility', 'none');
      }
      if (this.map.getLayer(this.layerId + '-labels')) {
        this.map.setLayoutProperty(this.layerId + '-labels', 'visibility', 'none');
      }
      if (this.map.getLayer(this.layerId + '-pk-labels')) {
        this.map.setLayoutProperty(this.layerId + '-pk-labels', 'visibility', 'none');
      }
    } catch (error) {
      console.warn('⚠️ Erreur lors de la désactivation des couches POI:', error);
    }
    
    this.visible = false;
    this.stopPeriodicReload();
    console.log('📍 Points d\'intérêt masqués + rechargement automatique arrêté');
  }

  toggle() {
    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  isVisible(): boolean {
    return this.visible;
  }

  forceReloadPK() {
    if (!this.map || !this.visible) return;
    
    console.log('🔄 Force reload PK demandé');
    // Forcer la mise à jour en réinitialisant les dernières valeurs
    this.lastUpdateBounds = null;
    this.lastUpdateZoom = -1;
    
    const bounds = this.map.getBounds();
    const zoom = this.map.getZoom();
    this.updatePKData(bounds, zoom);
  }

  // Configuration du chargement dynamique des PK
  private setupDynamicLoading() {
    if (!this.map) return;
    
    // Écouter les changements de vue (zoom et déplacement)
    this.map.on('moveend', this.handleMapMoveEnd.bind(this));
    this.map.on('zoomend', this.handleMapMoveEnd.bind(this));
  }

  private handleMapMoveEnd() {
    if (!this.map || !this.visible) return;
    
    const bounds = this.map.getBounds();
    const zoom = this.map.getZoom();
    
    // Éviter les mises à jour trop fréquentes avec un debounce plus long
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
    
    this.updateTimeout = window.setTimeout(() => {
      this.updatePKData(bounds, zoom);
    }, 1000); // Debounce de 1s pour réduire le clignotement
  }

  private boundsEqual(bounds1: maplibregl.LngLatBounds, bounds2: maplibregl.LngLatBounds): boolean {
    const sw1 = bounds1.getSouthWest();
    const ne1 = bounds1.getNorthEast();
    const sw2 = bounds2.getSouthWest();
    const ne2 = bounds2.getNorthEast();
    
    const precision = 0.001; // ~100m
    return Math.abs(sw1.lat - sw2.lat) < precision &&
           Math.abs(sw1.lng - sw2.lng) < precision &&
           Math.abs(ne1.lat - ne2.lat) < precision &&
           Math.abs(ne1.lng - ne2.lng) < precision;
  }

  private async updatePKData(bounds: maplibregl.LngLatBounds, zoom: number) {
    if (!this.map) return;
    
    // Vérifier si on a besoin de mettre à jour
    const boundsChanged = !this.lastUpdateBounds || 
      !this.boundsEqual(bounds, this.lastUpdateBounds);
    const zoomChanged = Math.abs(zoom - this.lastUpdateZoom) > 0.5;
    
    if (!boundsChanged && !zoomChanged) return;
    
    try {
      // Calculer zone étendue avec priorité dans la direction du mouvement
      const extendedBounds = this.calculateExtendedBounds(bounds);
      
      console.log('🛤️ Chargement PK pour région étendue:', {
        zoom,
        original: `${bounds.getSouth().toFixed(4)},${bounds.getWest().toFixed(4)} -> ${bounds.getNorth().toFixed(4)},${bounds.getEast().toFixed(4)}`,
        extended: `${extendedBounds.getSouth().toFixed(4)},${extendedBounds.getWest().toFixed(4)} -> ${extendedBounds.getNorth().toFixed(4)},${extendedBounds.getEast().toFixed(4)}`
      });
      
      // Charger les PK pour cette région étendue
      const pkPoints = await pkLoader.loadPKForRegion(extendedBounds, zoom);
      const pkData = convertPKsToPoiData(pkPoints);
      
      console.log(`🛤️ ${pkData.length} PK convertis pour affichage`);
      
      // Mettre à jour la source PK
      const source = this.map.getSource(this.pkSourceId) as maplibregl.GeoJSONSource;
      if (source) {
        source.setData({
          type: 'FeatureCollection',
          features: pkData.map(poi => ({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: poi.coordinates
            },
            properties: {
              id: poi.id,
              type: poi.type,
              name: poi.name,
              ...poi.properties
            }
          }))
        });
        
        console.log(`✅ Source PK mise à jour avec ${pkData.length} points`);
      } else {
        console.warn('⚠️ Source PK introuvable, recréation...');
        // Recréer les sources et couches si elles ont été supprimées par un changement de style
        await this.initializeLayer();
        // Relancer la mise à jour après recréation
        const source = this.map.getSource(this.pkSourceId) as maplibregl.GeoJSONSource;
        if (source) {
          source.setData({
            type: 'FeatureCollection',
            features: pkData.map((poi) => ({
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: poi.coordinates
              },
              properties: {
                id: poi.id,
                type: poi.type,
                name: poi.name,
                ...poi.properties
              }
            }))
          });
          console.log(`✅ Source PK recréée et mise à jour avec ${pkData.length} points`);
        }
      }
      
      this.lastUpdateBounds = bounds;
      this.lastUpdateZoom = zoom;
      
    } catch (error) {
      console.error('❌ Erreur mise à jour PK:', error);
    }
  }

  // Calculer zone étendue avec priorité dans la direction du mouvement
  private calculateExtendedBounds(originalBounds: maplibregl.LngLatBounds): maplibregl.LngLatBounds {
    const center = originalBounds.getCenter();
    const sw = originalBounds.getSouthWest();
    const ne = originalBounds.getNorthEast();
    
    // Calculer les dimensions actuelles
    const latSpan = ne.lat - sw.lat;
    const lngSpan = ne.lng - sw.lng;
    
    // Extension de base: 3x la taille visible (1.5x dans chaque direction)
    let baseLatExtension = latSpan * 1.5;
    let baseLngExtension = lngSpan * 1.5;
    
    // Extension directionnelle basée sur le bearing (direction du train)
    // Convertir bearing en radians pour les calculs trigonométriques
    const bearingRad = (this.currentBearing * Math.PI) / 180;
    
    // Calcul des extensions directionnelles
    // Plus d'extension dans la direction du mouvement, moins dans l'opposé
    const forwardLatExtension = baseLatExtension * (1 + 0.8 * Math.cos(bearingRad));
    const forwardLngExtension = baseLngExtension * (1 + 0.8 * Math.sin(bearingRad));
    const backwardLatExtension = baseLatExtension * (1 - 0.6 * Math.cos(bearingRad));
    const backwardLngExtension = baseLngExtension * (1 - 0.6 * Math.sin(bearingRad));
    
    // Créer les nouvelles bounds étendues
    const extendedSouth = center.lat - Math.max(forwardLatExtension, backwardLatExtension);
    const extendedNorth = center.lat + Math.max(forwardLatExtension, backwardLatExtension);
    const extendedWest = center.lng - Math.max(forwardLngExtension, backwardLngExtension);
    const extendedEast = center.lng + Math.max(forwardLngExtension, backwardLngExtension);
    
    return new maplibregl.LngLatBounds(
      [extendedWest, extendedSouth],
      [extendedEast, extendedNorth]
    );
  }

  // Méthode pour mettre à jour le bearing depuis l'extérieur
  updateBearing(bearing: number) {
    this.currentBearing = bearing;
  }

  // Méthode pour ajuster la fréquence de rechargement selon la vitesse de simulation
  setReloadFrequency(playbackSpeed: number) {
    if (playbackSpeed >= 30) {
      this.reloadFrequencyMultiplier = 3; // 3x plus souvent
    } else if (playbackSpeed >= 20) {
      this.reloadFrequencyMultiplier = 2; // 2x plus souvent
    } else {
      this.reloadFrequencyMultiplier = 1; // Normal
    }
    
    console.log(`🔄 Fréquence POI PK ajustée: x${this.reloadFrequencyMultiplier} (vitesse: x${playbackSpeed})`);
    
    // Redémarrer l'intervalle si nécessaire
    if (this.reloadInterval) {
      this.stopPeriodicReload();
      this.startPeriodicReload();
    }
  }

  // Démarrer le rechargement périodique
  private startPeriodicReload() {
    if (this.reloadInterval) return;
    
    // Calculer l'intervalle selon le multiplicateur (base: 30 secondes)
    const baseInterval = 30000; // 30 secondes
    const adjustedInterval = baseInterval / this.reloadFrequencyMultiplier;
    
    console.log(`🔄 Démarrage rechargement PK toutes les ${adjustedInterval/1000}s (multiplicateur: x${this.reloadFrequencyMultiplier})`);
    
    this.reloadInterval = window.setInterval(() => {
      if (this.visible && this.map) {
        const bounds = this.map.getBounds();
        const zoom = this.map.getZoom();
        console.log('🔄 Rechargement périodique des PK');
        this.updatePKData(bounds, zoom);
      }
    }, adjustedInterval);
  }

  // Arrêter le rechargement périodique
  private stopPeriodicReload() {
    if (this.reloadInterval) {
      clearInterval(this.reloadInterval);
      this.reloadInterval = null;
    }
  }

  destroy() {
    if (!this.map) return;
    
    // Nettoyer les timeouts et intervals
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
    this.stopPeriodicReload();
    
    try {
      this.map.removeLayer(this.layerId + '-pk-labels');
      this.map.removeLayer(this.layerId + '-labels');
      this.map.removeLayer(this.layerId);
      this.map.removeSource(this.pkSourceId);
      this.map.removeSource(this.sourceId);
    } catch (e) {
      console.warn('Erreur lors de la suppression de la couche POI:', e);
    }
  }
}

export const poiLayer = new PoiLayer();